import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAdminMe, getExecutiveStats, listExecutives, createExecutive, updateExecutive, updateExecutiveStatus } from '../api';
import AdminShell from './AdminShell';
import '../styles/AdminDashboard.css';
import '../styles/GlassAdmin.css';
function formatNumber(value) {
  if (value === null || value === undefined) return '—';
  return new Intl.NumberFormat('en-IN').format(value);
}

function initials(name) {
  if (!name) return '?';
  const parts = name.trim().split(' ');
  return ((parts[0]?.[0] || '') + (parts[1]?.[0] || '')).toUpperCase();
}

const EMPTY_FORM = { name: '', email: '', executiveCode: '' };

export default function SalesExecutives() {
  const navigate = useNavigate();
  const [admin, setAdmin] = useState(null);
  const [stats, setStats] = useState(null);
  const [executives, setExecutives] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [formError, setFormError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const loadExecutives = () => {
    listExecutives().then(({ data }) => setExecutives(data.executives)).catch(() => {});
    getExecutiveStats().then(({ data }) => setStats(data.stats)).catch(() => {});
  };

  useEffect(() => {
    getAdminMe()
      .then(({ data }) => setAdmin(data.admin))
      .catch(() => {
        localStorage.removeItem('adminToken');
        sessionStorage.removeItem('adminToken');
        navigate('/login');
      });

    setLoading(true);
    Promise.all([listExecutives(), getExecutiveStats()])
      .then(([execRes, statsRes]) => {
        setExecutives(execRes.data.executives);
        setStats(statsRes.data.stats);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [navigate]);

  const handleToggleStatus = async (exec) => {
    const newStatus = exec.status === 'active' ? 'inactive' : 'active';
    try {
      await updateExecutiveStatus(exec.id, newStatus);
      loadExecutives();
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to update status.');
    }
  };

  const openCreateModal = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setFormError('');
    setShowModal(true);
  };

  const openEditModal = (exec) => {
    setEditingId(exec.id);
    setForm({ name: exec.name, email: exec.email, executiveCode: exec.executive_code });
    setFormError('');
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError('');
    if (!form.name.trim() || !form.email.trim() || !form.executiveCode.trim()) {
      setFormError('All fields are required.');
      return;
    }
    setSubmitting(true);
    try {
      if (editingId) {
        await updateExecutive(editingId, form.name.trim(), form.email.trim(), form.executiveCode.trim());
      } else {
        await createExecutive(form.name.trim(), form.email.trim(), form.executiveCode.trim());
      }
      setShowModal(false);
      setForm(EMPTY_FORM);
      setEditingId(null);
      loadExecutives();
    } catch (err) {
      setFormError(err.response?.data?.message || 'Failed to save executive.');
    } finally {
      setSubmitting(false);
    }
  };

  const exportCsv = () => {
    const header = 'Name,Email,Executive Code,Total Registrations,Converted,Status\n';
    const rows = executives
      .map((e) => [e.name, e.email, e.executive_code, e.total_registrations, e.converted, e.status].join(','))
      .join('\n');
    const blob = new Blob([header + rows], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'sales_executives.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const filteredExecutives = executives.filter((exec) => {
    const q = searchTerm.trim().toLowerCase();
    if (!q) return true;
    return (
      exec.name.toLowerCase().includes(q) ||
      exec.email.toLowerCase().includes(q) ||
      exec.executive_code.toLowerCase().includes(q)
    );
  });

  return (
    <AdminShell
    
      admin={admin}
      badges={{}}
      pageTitle="Sales Executives"
      searchValue={searchTerm}
      onSearchChange={setSearchTerm}
      searchPlaceholder="Search executives by name, email, or code..."
    >
    <div className="glass-page">
      <div className="dash-welcome-row">
        <div>
          <h2 className="dash-welcome-title">Executive Management</h2>
          <p className="dash-welcome-subtitle">Monitor and manage your sales team performance.</p>
        </div>
        <div className="dash-quick-actions">
          <button className="dash-quick-btn primary" onClick={openCreateModal}>
            <span className="material-symbols-outlined" style={{ fontSize: 18 }}>person_add</span>
            Create Executive ID
          </button>
          <button className="dash-quick-btn secondary" onClick={exportCsv}>
            <span className="material-symbols-outlined" style={{ fontSize: 18 }}>download</span>
            Export List
          </button>
        </div>
      </div>

      <div className="glass-kpi-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))' }}>
        <div className="glass-kpi-card">
          <div className="dash-kpi-top">
            <div className="dash-kpi-icon"><span className="material-symbols-outlined">groups</span></div>
          </div>
          <p className="dash-kpi-label">Total Executives</p>
          <h3 className="dash-kpi-value">{formatNumber(stats?.totalExecutives)}</h3>
        </div>

        <div className="glass-kpi-card">
          <div className="dash-kpi-top">
            <div className="dash-kpi-icon tertiary"><span className="material-symbols-outlined">workspace_premium</span></div>
          </div>
          <p className="dash-kpi-label">Top Performer</p>
          <h3 className="dash-panel-title" style={{ fontSize: 20, marginTop: 8 }}>
            {stats?.topPerformer ? `${stats.topPerformer.name} (${stats.topPerformer.registrations})` : '—'}
          </h3>
        </div>

        <div className="glass-kpi-card">
          <div className="dash-kpi-top">
            <div className="dash-kpi-icon"><span className="material-symbols-outlined">trending_up</span></div>
          </div>
          <p className="dash-kpi-label">Total Registrations</p>
          <h3 className="dash-kpi-value">{formatNumber(stats?.totalRegistrations)}</h3>
        </div>
      </div>

      <div className="glass-directory-panel">
        <div className="dash-directory-header">
          <h3 className="dash-panel-title">Executive Directory</h3>
        </div>
        <div className="dash-directory-table-wrap">
          {loading ? (
            <div className="dash-empty-state">Loading executives...</div>
          ) : filteredExecutives.length === 0 ? (
            <div className="dash-empty-state">
              {searchTerm ? 'No executives match your search.' : 'No sales executives yet. Create one to get started.'}
            </div>
          ) : (
            <table className="dash-directory-table">
              <thead>
                <tr>
                  <th>Executive Name</th>
                  <th>Executive ID</th>
                  <th>Total Registrations</th>
                  <th>Converted</th>
                  <th>Status</th>
                  <th className="right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredExecutives.map((exec) => (
                  <tr key={exec.id}>
                    <td>
                      <div className="dash-exec-row">
                        <div className={`dash-exec-avatar ${exec.status === 'inactive' ? 'inactive' : ''}`}>
                          {initials(exec.name)}
                        </div>
                        <div>
                          <p className="dash-exec-name">{exec.name}</p>
                          <p className="dash-exec-email">{exec.email}</p>
                        </div>
                      </div>
                    </td>
                    <td>{exec.executive_code}</td>
                    <td>{exec.total_registrations}</td>
                    <td>{exec.converted}</td>
                    <td>
                      <span className={`dash-status-pill ${exec.status}`}>
                        {exec.status === 'active' ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="dash-row-actions">
                      <button className="dash-link-btn edit" onClick={() => openEditModal(exec)}>Edit</button>
                      <button
                        className={`dash-link-btn ${exec.status === 'active' ? 'deactivate' : 'activate'}`}
                        onClick={() => handleToggleStatus(exec)}
                      >
                        {exec.status === 'active' ? 'Deactivate' : 'Activate'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {showModal && (
        <div className="dash-modal-backdrop" onClick={() => setShowModal(false)}>
          <div className="dash-modal" onClick={(e) => e.stopPropagation()}>
            <h3 className="dash-modal-title">{editingId ? 'Edit Executive' : 'Create Executive ID'}</h3>
            {formError && <div className="admin-banner error">{formError}</div>}
            <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <div className="dash-modal-field">
                <label htmlFor="execName">Full Name</label>
                <input
                  id="execName"
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="e.g. Priya Nair"
                />
              </div>
              <div className="dash-modal-field">
                <label htmlFor="execEmail">Email</label>
                <input
                  id="execEmail"
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  placeholder="priya.n@milieu.com"
                />
              </div>
              <div className="dash-modal-field">
                <label htmlFor="execCode">Executive Code</label>
                <input
                  id="execCode"
                  type="text"
                  value={form.executiveCode}
                  onChange={(e) => setForm({ ...form, executiveCode: e.target.value })}
                  placeholder="MG-SE-004"
                />
              </div>
              <div className="dash-modal-actions">
                <button type="button" className="dash-modal-btn secondary" onClick={() => setShowModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="dash-modal-btn primary" disabled={submitting}>
                  {submitting ? 'Saving...' : editingId ? 'Save Changes' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      </div>
    </AdminShell>
  );
}